import { useState } from "react";
import { Download, Smartphone, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PWA } from "@/constants/testIds/pwa";
import { isViewer } from "@/lib/guards";
import { toast } from "sonner";
import { friendlyError } from "@/lib/errors";

export default function PwaProfileSection({
  user, standalone, ios, config, prefs, onInstall, onPatchPrefs, onTestPush,
}) {
  const viewer = isViewer(user);
  const pushOk = Boolean(config?.push_enabled) && (standalone || !ios);
  const showIosHint = Boolean(config?.push_enabled) && ios && !standalone;
  const [testing, setTesting] = useState(false);

  const testPush = async () => {
    if (!onTestPush) return;
    setTesting(true);
    try {
      await onTestPush();
      toast.success("Aviso enviado ✦ Olha as notificações.");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setTesting(false);
    }
  };

  return (
    <section
      id="app-celular"
      data-testid={PWA.profileSection}
      className="bg-brand-surface border border-brand-line rounded-3xl p-5 sm:p-6"
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="shrink-0 w-9 h-9 rounded-xl bg-brand-elevated text-brand-rose flex items-center justify-center">
          <Smartphone className="w-4 h-4" />
        </span>
        <div>
          <h2 className="font-display text-xl text-brand-ink leading-tight">App no celular</h2>
          <p className="text-[12.5px] text-brand-ink2 font-ui mt-0.5">
            {standalone ? "App na tela inicial ✦" : "Ainda não estou na sua tela inicial."}
          </p>
        </div>
      </div>

      {!standalone && (
        <button
          type="button"
          data-testid={PWA.profileInstall}
          onClick={onInstall}
          className="w-full min-h-[44px] rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[14px] font-ui inline-flex items-center justify-center gap-2 mb-4"
        >
          <Download className="w-4 h-4" /> Baixar o app
        </button>
      )}

      {showIosHint && (
        <p className="text-[12.5px] text-brand-ink2 font-ui">
          Instala o app primeiro pra eu te avisar de manhã.
        </p>
      )}
      {pushOk && typeof Notification !== "undefined" && Notification.permission === "denied" && (
        <p className="text-[12.5px] text-brand-gold font-ui mb-3">
          Libera o aviso nas configurações do celular pra eu te chamar às 8h.
        </p>
      )}

      {pushOk && !viewer && (
        <div className="space-y-3">
          <label className="flex items-center justify-between gap-3 min-h-[44px]">
            <span className="text-[14px] text-brand-ink font-ui">Calendário do dia, 8h</span>
            <Switch
              data-testid={PWA.prefDaily}
              checked={Boolean(prefs?.daily_calendar)}
              onCheckedChange={(v) => onPatchPrefs({ daily_calendar: v })}
            />
          </label>
          <label className="flex items-center justify-between gap-3 min-h-[44px]">
            <span className="text-[14px] text-brand-ink font-ui">Pendências da semana, segunda 8h</span>
            <Switch
              data-testid={PWA.prefWeekly}
              checked={Boolean(prefs?.weekly_pending)}
              onCheckedChange={(v) => onPatchPrefs({ weekly_pending: v })}
            />
          </label>
          <button
            type="button"
            data-testid={PWA.testPush}
            onClick={testPush}
            disabled={testing}
            className="w-full min-h-[44px] rounded-full border border-brand-line bg-brand-elevated text-brand-ink hover:text-brand-rose hover:border-brand-rose text-[13.5px] font-ui inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Bell className="w-4 h-4" /> {testing ? "Enviando…" : "Mandar um aviso de teste"}
          </button>
        </div>
      )}
    </section>
  );
}
